import { useState } from "react"
import { Link } from "react-router-dom"
import GuardianIcon from "@/components/common/GuardianIcon"
import { claimAlmanacMilestone } from "@/api/homeMotivationApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import {
  HomeAlmanacMotivation,
  HomeMotivationStatus,
} from "@/types/homeMotivation"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

type Props = {
  almanac: HomeAlmanacMotivation
  onUpdated: (status: HomeMotivationStatus) => void
}

export default function AlmanacHomeProgressCard({ almanac, onUpdated }: Props) {
  const { applyPassport, refreshPassport } = useGuardiansPassport()
  const [collapsed, setCollapsed] = useState(true)
  const [claiming, setClaiming] = useState<number | null>(null)

  const progress = Math.min(100, Math.max(0, almanac.progressPercent || 0))
  const claimable = almanac.milestones.find((m) => m.claimable)

  const onClaim = async (percent: 25 | 50 | 75 | 95 | 100) => {
    setClaiming(percent)
    try {
      const result = await claimAlmanacMilestone(percent)
      onUpdated(result.status)
      applyPassport({
        coins: result.coins,
        chakraCharges: result.chakraCharges,
        fuseBoosters: result.fuseBoosters,
      })
      await refreshPassport()
      window.alert(result.title)
    } catch (e) {
      window.alert(getGameApiErrorMessage(e, "Не удалось забрать"))
    } finally {
      setClaiming(null)
    }
  }

  return (
    <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-3.5">
      <button
        type="button"
        className="flex w-full flex-row items-center gap-2.5 text-left"
        onClick={() => setCollapsed((v) => !v)}
      >
        <GuardianIcon name="almanac" size={52} />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-amber-300">
            Альманах
          </div>
          <div className="text-[15px] font-extrabold text-white">
            {almanac.stampedSlots} / {almanac.totalSlots} печатей
          </div>
          <div className="text-xs font-semibold text-white/55">
            {Math.round(progress)}% ·{" "}
            {almanac.milestones.filter((m) => m.claimed).length}/
            {almanac.milestones.length} вех
          </div>
        </div>
        <span className="text-white/45">{collapsed ? "▼" : "▲"}</span>
      </button>

      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-amber-400"
          style={{ width: `${progress}%` }}
        />
      </div>

      {!collapsed && (
        <div className="mt-3 flex flex-col gap-2">
          {almanac.milestones.map((m) => (
            <div
              key={m.percent}
              className="flex flex-row items-center justify-between gap-2 text-sm"
            >
              <span className={m.claimed ? "text-white/45" : "text-white/85"}>
                {m.percent}% · +{m.rewardCoins} монет
              </span>
              {m.claimable ? (
                <button
                  type="button"
                  disabled={claiming === m.percent}
                  onClick={() => void onClaim(m.percent)}
                  className="rounded-lg bg-guardians-accent px-2 py-1 text-xs font-bold text-guardians-bg"
                >
                  Забрать
                </button>
              ) : m.claimed ? (
                <span className="text-xs text-green-400">✓</span>
              ) : null}
            </div>
          ))}
          <Link
            to="/play/almanac"
            className="text-center text-xs font-semibold text-guardians-accent"
          >
            Открыть альманах
          </Link>
        </div>
      )}

      {collapsed && claimable ? (
        <button
          type="button"
          disabled={claiming != null}
          onClick={() => void onClaim(claimable.percent)}
          className="mt-2 w-full rounded-xl bg-guardians-accent py-2 text-sm font-extrabold text-guardians-bg"
        >
          Забрать веху {claimable.percent}%
        </button>
      ) : null}
    </div>
  )
}
